import { useMutation } from '@apollo/client/react';
import { get } from 'lodash-es';
import pluralize from 'pluralize';
import { type FC, type ReactNode } from 'react';

import { Headline, Icon, Popover } from '@zen/DesignSystem';
import type { SuggestionSource } from '@zen/graphql/types.generated';
import { useNotification } from '@zen/utils/hooks/useNotification';
import { performMutation } from '@zen/utils/performMutation';

import {
  AcceptSuggestionDocument,
  type AcceptSuggestionMutationResult,
  type AcceptSuggestionMutationVariables,
  RejectSuggestionDocument,
  type RejectSuggestionMutationResult,
  type RejectSuggestionMutationVariables,
  RejectSuggestionsDocument,
  type RejectSuggestionsMutationResult,
  type RejectSuggestionsMutationVariables
} from './graphql';
import { enumToSentenceCase } from './helpers';
import Suggestion from './Suggestion';
import { SuggestionsList } from './SuggestionsList/SuggestionsList';
import type { Suggestion as SuggestionType } from './types';

interface Props {
  currentValue: string;
  isDocumentSuggestion?: boolean;
  originalValuePresent: boolean;
  refetchQueries: string[];
  suggestions: SuggestionType[];
}

const Suggestions: FC<Props> = ({
  currentValue,
  isDocumentSuggestion = false,
  originalValuePresent,
  refetchQueries,
  suggestions
}) => {
  const { addError, addSuccess } = useNotification();

  const [acceptSuggestion] = useMutation<AcceptSuggestionMutationResult, AcceptSuggestionMutationVariables>(
    AcceptSuggestionDocument
  );

  const [rejectSuggestion] = useMutation<RejectSuggestionMutationResult, RejectSuggestionMutationVariables>(
    RejectSuggestionDocument
  );

  const [rejectSuggestions] = useMutation<RejectSuggestionsMutationResult, RejectSuggestionsMutationVariables>(
    RejectSuggestionsDocument
  );

  const handleAcceptSuggestion = async (suggestionId: string) => {
    await performMutation({
      mutationFn: () =>
        acceptSuggestion({
          awaitRefetchQueries: true,
          refetchQueries: [...refetchQueries, 'getSuggestions'],
          variables: {
            suggestionId
          }
        }),
      onError: (errors) => {
        addError(get(errors, '[0].message', 'Suggestion could not be accepted.'));
      },
      onSuccess: () => {
        addSuccess(`Suggestion accepted`);
      }
    });
  };

  const handleRejectSuggestion = async (suggestionId: string, rejectReason: string) => {
    await performMutation({
      mutationFn: () =>
        rejectSuggestion({
          awaitRefetchQueries: true,
          refetchQueries: [...refetchQueries, 'getSuggestions'],
          variables: {
            input: {
              rejectReason,
              suggestionId
            }
          }
        }),
      onError: (errors) => {
        addError(get(errors, '[0].message', 'Suggestion could not be rejected.'));
      },
      onSuccess: () => {
        addSuccess(`Suggestion rejected`);
      }
    });
  };

  const handleRejectSuggestions = async (suggestionIds: string[]) => {
    await performMutation({
      mutationFn: () =>
        rejectSuggestions({
          awaitRefetchQueries: true,
          refetchQueries: [...refetchQueries, 'getSuggestions'],
          variables: {
            input: {
              rejectReason: 'Rejected',
              suggestionIds
            }
          }
        }),
      onError: (errors) => {
        addError(get(errors, '[0].message', 'Suggestion could not be rejected.'));
      },
      onSuccess: () => {
        addSuccess(`Suggestions rejected`);
      }
    });
  };

  const renderSuggestions = (): ReactNode => {
    const targetAttribute = enumToSentenceCase(suggestions[0].targetAttribute).toLowerCase();

    return (
      <div className="bg-sunset-light p-0.5 rounded-md">
        <div className="bg-white rounded-sm flex flex-col p-6">
          <Headline className="mb-1" level={3}>
            Review and confirm {targetAttribute}
          </Headline>

          <p>We've found multiple {pluralize(targetAttribute)}.</p>
          <p>
            Please review and select the correct one.{' '}
            {currentValue && (
              <>
                {' '}
                The current value is <span className="font-bold">{currentValue}</span>.
              </>
            )}
          </p>

          <div className="mt-4">
            <SuggestionsList
              onAccept={handleAcceptSuggestion}
              onReject={handleRejectSuggestions}
              suggestions={suggestions}
              zencargoReference={suggestions[0].parentEntityId}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderSuggestionPopover = () => {
    return (
      <Popover popover={renderSuggestions} renderInPortal={true} placement="top" popoverClassNames="min-w-150">
        <div className="bg-sunset-light rounded-4xl flex items-center px-2 gap-2 justify-between w-auto h-8 text-navy-dark mt-2 cursor-pointer">
          <div className="flex items-center gap-2 font-bold">
            <Icon icon="zicon-sparkles-empty" />
            {suggestions?.length} suggestions
          </div>
        </div>
      </Popover>
    );
  };

  if (suggestions.length > 1) {
    return renderSuggestionPopover();
  }

  const variant = originalValuePresent || isDocumentSuggestion ? 'pill' : 'text';

  if (suggestions.length === 1) {
    const suggestion = suggestions[0];

    return (
      <Suggestion
        id={suggestion.id}
        onAccept={handleAcceptSuggestion}
        onReject={handleRejectSuggestion}
        proposedValue={suggestion.proposedValue as string}
        sources={suggestion.sources as SuggestionSource[]}
        targetAttribute={suggestion.targetAttribute}
        variant={variant}
      />
    );
  }
};

export default Suggestions;
